import * as React from "react";

import { Button } from "@repo/ui/components/button";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation } from "convex/react";
import { makeFunctionReference } from "convex/server";
import { Loader2 } from "lucide-react";
import { z } from "zod";

const createScannerAuthToken = makeFunctionReference<"mutation">(
  "mediaDayPhotos:createScannerAuthToken",
);

const desktopApps = {
  "set-scanner": {
    name: "Media Day Scanner",
    scheme: "media-day-scanner://auth",
  },
  renamer: {
    name: "Media Day Renamer",
    scheme: "media-day-renamer://auth",
  },
} as const;

export const Route = createFileRoute("/_authed/scanner-auth")({
  component: RouteComponent,
  validateSearch: z.object({
    app: z.enum(["set-scanner", "renamer"]).optional(),
    state: z.string().optional(),
  }),
});

function RouteComponent() {
  const { app = "set-scanner", state } = Route.useSearch();
  const desktopApp = desktopApps[app];
  const createToken = useMutation(createScannerAuthToken);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    void createToken({ app })
      .then(({ token, user }) => {
        if (cancelled) return;
        const callback = new URL(desktopApp.scheme);
        callback.searchParams.set("token", token);
        callback.searchParams.set("convexUrl", import.meta.env.VITE_CONVEX_URL as string);
        if (user.name) callback.searchParams.set("name", user.name);
        if (user.email) callback.searchParams.set("email", user.email);
        if (user.image) callback.searchParams.set("image", user.image);
        if (state) callback.searchParams.set("state", state);
        window.location.assign(callback.toString());
      })
      .catch((caught) => {
        if (!cancelled) setError(caught instanceof Error ? caught.message : String(caught));
      });
    return () => {
      cancelled = true;
    };
  }, [app, createToken, desktopApp.scheme, state]);

  return (
    <div className="flex min-h-[calc(100svh-3.5rem)] items-center justify-center p-6">
      <div className="w-full max-w-sm rounded-lg border bg-card p-6 text-center shadow-sm">
        {error ? (
          <>
            <h1 className="text-lg font-semibold">Could not sign in {desktopApp.name}</h1>
            <p className="mt-2 text-sm text-muted-foreground">{error}</p>
          </>
        ) : (
          <>
            <Loader2 className="mx-auto size-6 animate-spin text-muted-foreground" />
            <h1 className="mt-4 text-lg font-semibold">Opening {desktopApp.name}…</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              If nothing happens, make sure {desktopApp.name} is installed and open.
            </p>
            <Button className="mt-4" onClick={() => window.location.reload()} variant="outline">
              Try again
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
